import assign from "lodash.assign";
import omit from "lodash.omit";
import isEmpty from "lodash.isempty";
import { browserHistory } from "react-router";

import Entitlements from "services/Entitlements";
import Promo from "services/Promo";
import GoogleTracking from "services/GoogleTracking";
import Payments from "services/Payments";
import { setThinking, doneThinking } from "ducks/thinking";
import marketingActions from "ducks/marketing";
import paymentFormActions from "ducks/paymentForm";
import appPath from "utils/appPath";
import optimizely from "utils/optimizely";
import { getDefaultDigitalRatePlan } from "utils/appConfig";
import { setErrorInErrors, clearErrorFromErrors } from "utils/errorsUtil";
import { isOnboardingEnabled } from "utils/FeatureFlags";
import {
    SCREENS,
    OFFER_TYPES,
    ELOQUA_ABANDONED_CART_STATUS,
    OPTIMIZELY_CUSTOM_EVENTS,
    PROMOTION_TYPES, ABBA_CONFIGS
} from "common/constants/Constants";
import { THINKING_TYPE_PROCESSING } from "common/constants/v2/Constants";
import Validation from "common/utils/Validation";
import ERROR_MESSAGES from "common/constants/ErrorMessages";
import Token from "services/Token";
import REDUX_ERROR_TYPES from "constants/ReduxErrorTypes";
import PayPal from "services/PayPal";

// Actions (must be name-spaced for reducer)

export const SUBSCRIPTION_FORM_SET_PRIMARY_PRODUCT_DETAILS = "SUBSCRIPTION_FORM_SET_PRIMARY_PRODUCT_DETAILS";
export const SUBSCRIPTION_FORM_SET_SECONDARY_PRODUCT_DETAILS = "SUBSCRIPTION_FORM_SET_SECONDARY_PRODUCT_DETAILS";
export const SUBSCRIPTION_FORM_SET_SELECTED_PRODUCT_DETAILS = "SUBSCRIPTION_FORM_SET_SELECTED_PRODUCT_DETAILS";
export const SUBSCRIPTION_FORM_SET_PROMO_INFO = "SUBSCRIPTION_FORM_SET_PROMO_INFO";
export const SUBSCRIPTION_FORM_PROMO_CLEAR = "SUBSCRIPTION_FORM_PROMO_CLEAR";
export const SUBSCRIPTION_FORM_SET_FIRST_NAME = "SUBSCRIPTION_FORM_SET_FIRST_NAME";
export const SUBSCRIPTION_FORM_SET_LAST_NAME = "SUBSCRIPTION_FORM_SET_LAST_NAME";
export const SUBSCRIPTION_FORM_SET_ERROR = "SUBSCRIPTION_FORM_SET_ERROR";
export const SUBSCRIPTION_FORM_CLEAR_ERROR = "SUBSCRIPTION_FORM_CLEAR_ERROR";
export const SUBSCRIPTION_FORM_SET_CANCEL_NOTICE_CHECKED = "SUBSCRIPTION_FORM_SET_CANCEL_NOTICE_CHECKED";

// Reducer

const DEFAULT_STATE = {
    primaryProductDetails: {},
    secondaryProductDetails: {},
    selectedProductDetails: {},
    errors: {},
    cancelNoticeChecked: false
};

export function subscriptionFormReducer(state = DEFAULT_STATE, action) {
    switch (action.type) {
        case SUBSCRIPTION_FORM_SET_PRIMARY_PRODUCT_DETAILS: {
            return assign({}, state, {
                primaryProductDetails: {
                    productName: action.productName,
                    productCategory: action.productCategory,
                    productType: action.productType,
                    offerType: action.offerType,
                    productId: action.productId,
                    ratePlanId: action.ratePlanId,
                    pricing: action.pricing,
                    billingPeriod: action.billingPeriod,
                    promotionType: action.promotionType
                }
            });
        }
        case SUBSCRIPTION_FORM_SET_SECONDARY_PRODUCT_DETAILS: {
            return assign({}, state, {
                secondaryProductDetails: {
                    productName: action.productName,
                    productCategory: action.productCategory,
                    productType: action.productType,
                    offerType: action.offerType,
                    productId: action.productId,
                    ratePlanId: action.ratePlanId,
                    pricing: action.pricing,
                    billingPeriod: action.billingPeriod,
                    promotionType: action.promotionType
                }
            });
        }
        case SUBSCRIPTION_FORM_SET_PROMO_INFO:
            return assign({}, state, {
                promoCodeInfo: action.promoCodeInfo
            });
        case SUBSCRIPTION_FORM_PROMO_CLEAR:
            return assign({}, state, {
                promoCodeInfo: null
            });
        case SUBSCRIPTION_FORM_SET_FIRST_NAME:
            return assign({}, state, {
                firstName: action.firstName
            });
        case SUBSCRIPTION_FORM_SET_LAST_NAME:
            return assign({}, state, {
                lastName: action.lastName
            });
        case SUBSCRIPTION_FORM_SET_ERROR:
            return assign({}, state, {
                errors: setErrorInErrors(state.errors, action.error)
            });
        case SUBSCRIPTION_FORM_CLEAR_ERROR:
            return assign({}, state, {
                errors: clearErrorFromErrors(state.errors, action.errorType)
            });
        case SUBSCRIPTION_FORM_SET_CANCEL_NOTICE_CHECKED:
            return assign({}, state, {
                cancelNoticeChecked: action.checked
            });
        case SUBSCRIPTION_FORM_SET_SELECTED_PRODUCT_DETAILS:
            return assign({}, state, {
                selectedProductDetails: action.selectedProductDetails
            });
        default:
            return state;
    }
}

// Action Creators

export function getPrimaryProductDetails(id) {
    return (dispatch, getState) => {
        return Entitlements.getProductRatePlanInformation(id)
            .then((response) => {
                const { ratePlan, promotion } = response;

                if (ratePlan.offerType === OFFER_TYPES.GROUP) {
                    return window.location.replace(`${ getState().config.bloombergSiteUrl }/subscriptions`);
                }

                optimizely.sendUserRatePlanInfo(ratePlan);
                dispatch(setPrimaryProductDetails(ratePlan));
                dispatch(setSelectedProductDetails(ratePlan));

                if (promotion) {
                    dispatch(setPromoCodeInfo({ promotion }));
                }
            })
            .catch((error) => {
                if (error.redirectTo) {
                    return window.location.replace(error.redirectTo);
                }

                console.error("unable to getProductDetails", error);

                throw error;
            });
    };
}

export function getSecondaryProductDetails(id) {
    return (dispatch) => {
        return Entitlements.getProductRatePlanInformation(id)
            .then((response) => {
                const { ratePlan } = response;

                dispatch(setSecondaryProductDetails(ratePlan));
            })
            .catch((error) => {
                console.error("unable to getSecondaryProductDetails", error);
            });
    };
}

export function setPrimaryProductDetails(props) {
    const {
        productName,
        productCategory,
        productType,
        offerType,
        productId,
        ratePlanId,
        pricing,
        billingPeriod,
        promotionType
    } = props;

    return {
        type: SUBSCRIPTION_FORM_SET_PRIMARY_PRODUCT_DETAILS,
        productName,
        productCategory,
        productType,
        offerType,
        productId,
        ratePlanId,
        pricing,
        billingPeriod,
        promotionType: PROMOTION_TYPES[promotionType] || PROMOTION_TYPES["NONE"]
    };
}

export function setSecondaryProductDetails(props) {
    const {
        productName,
        productCategory,
        productType,
        offerType,
        productId,
        ratePlanId,
        pricing,
        billingPeriod,
        promotionType
    } = props;

    return {
        type: SUBSCRIPTION_FORM_SET_SECONDARY_PRODUCT_DETAILS,
        productName,
        productCategory,
        productType,
        offerType,
        productId,
        ratePlanId,
        pricing,
        billingPeriod,
        promotionType: PROMOTION_TYPES[promotionType] || PROMOTION_TYPES["NONE"]
    };
}

export function setSelectedProductDetails(selectedProductDetails) {
    return {
        type: SUBSCRIPTION_FORM_SET_SELECTED_PRODUCT_DETAILS,
        selectedProductDetails
    };
}

export function getPromoCodeInfo(promoCode) {
    return (dispatch) => {
        return Promo.getPromoCodeInfo(promoCode)
            .then((promoCodeInfo) => {
                const {
                    promotion: {
                        ratePlanId
                    }
                } = promoCodeInfo;

                dispatch(setPromoCodeInfo(promoCodeInfo));

                return dispatch(getPrimaryProductDetails(ratePlanId));
            })
            .catch(() => {
                dispatch(clearPromoCodeInfo());

                return dispatch(getPrimaryProductDetails(getDefaultDigitalRatePlan()));
            });
    };
}

export function clearPromoCodeInfo() {
    return {
        type: SUBSCRIPTION_FORM_PROMO_CLEAR
    };
}

export function setPromoCodeInfo(promoCodeInfo) {
    return {
        type: SUBSCRIPTION_FORM_SET_PROMO_INFO,
        promoCodeInfo
    };
}

export function setCancelNoticeChecked(checked) {
    return {
        type: SUBSCRIPTION_FORM_SET_CANCEL_NOTICE_CHECKED,
        checked
    };
}

export function getReferralToken(referralToken) {
    return (dispatch) => {
        return Token.getProductRatePlanInformationByReferralToken(referralToken)
            .then((ratePlan) => {
                optimizely.sendUserRatePlanInfo(ratePlan);
                dispatch(setPrimaryProductDetails(ratePlan));
                dispatch(setSelectedProductDetails(ratePlan));
            })
            .catch((error) => {
                if (error.redirectTo) {
                    return window.location.replace(error.redirectTo);
                }

                console.error("fetching rate plan info by referral token failed", error);

                throw error;
            });
    };
}

export function updateFirstName(firstName) {
    return {
        type: SUBSCRIPTION_FORM_SET_FIRST_NAME,
        firstName
    };
}

export function updateLastName(lastName) {
    return {
        type: SUBSCRIPTION_FORM_SET_LAST_NAME,
        lastName
    };
}

export function validateFirstName() {
    return (dispatch, getState) => {
        const { subscriptionForm: { firstName } } = getState();
        const error = Validation.validateFieldIsRequired(firstName)
            || Validation.validateEnglishCharacters(firstName);

        if (error) {
            dispatch(setError({
                type: "firstName",
                message: error.message
            }));

            return false;
        }

        dispatch(clearError("firstName"));

        return true;
    };
}

export function validateLastName() {
    return (dispatch, getState) => {
        const { subscriptionForm: { lastName } } = getState();
        const error = Validation.validateFieldIsRequired(lastName)
            || Validation.validateEnglishCharacters(lastName);

        if (error) {
            dispatch(setError({
                type: "lastName",
                message: error.message
            }));

            return false;
        }

        dispatch(clearError("lastName"));

        return true;
    };
}

export function validateIsCancelNoticeChecked() {
    return (dispatch, getState) => {
        const {
            abba: {
                config: {
                    [ABBA_CONFIGS.SUBSCRIPTION_FORM]: {
                        hideLegalDisclaimer
                    } = {}
                }
            },
            subscriptionForm: { cancelNoticeChecked },
            billingAddressForm:{
                state : billingAddressState
            },
            shippingAddressForm:{
                state : shippingAddressState
            }
        } = getState();

        if (!hideLegalDisclaimer) {
            const error = Validation.validateCancelNoticeChecked(billingAddressState, shippingAddressState, cancelNoticeChecked);

            if (error) {
                dispatch(setError({
                    type: REDUX_ERROR_TYPES.CANCEL_NOTICE_UNCHECKED,
                    message: error.message
                }));

                return false;
            }
        }

        dispatch(clearError(REDUX_ERROR_TYPES.CANCEL_NOTICE_UNCHECKED));

        return true;
    };
}

export function setError(error) {
    return {
        type: SUBSCRIPTION_FORM_SET_ERROR,
        error
    };
}

export function clearError(errorType) {
    return {
        type: SUBSCRIPTION_FORM_CLEAR_ERROR,
        errorType
    };
}

export function collectSinglePageErrors() {
    return (dispatch, getState) => {
        const {
            subscriptionForm: {
                errors: subscriptionFormErrors
            } = {},
            paymentForm: {
                errors: paymentFormErrors
            } = {},
            billingAddressForm: {
                errors: billingAddressFormErrors
            } = {},
            shippingAddressForm: {
                errors: shippingAddressFormErrors
            } = {},
            loginRegister: {
                errors: loginRegisterErrors
            } = {}
        } = getState();

        /**
         * Show a generic "fields required" error for the cases where field-level errors don't make sense in the
         * module error context excluding cancelNoticeCheck which we check on submit
         */
        const error = getSubscriptionFormError(omit(subscriptionFormErrors, REDUX_ERROR_TYPES.CANCEL_NOTICE_UNCHECKED))
            || getError(loginRegisterErrors)
            || getError(paymentFormErrors)
            || getGenericError(billingAddressFormErrors)
            || getGenericError(shippingAddressFormErrors);

        if (error) {
            dispatch(setError({ type: REDUX_ERROR_TYPES.SINGLE_PAGE_MODULE_LEVEL, message: error.message }));
        }
    };
}

function getSubscriptionFormError(errors) {
    return errors.purchaseSubscription || getGenericError(errors);
}

function getError(errors) {
    if (!isEmpty(errors)) {
        return errors[Object.keys(errors)[0]];
    }
}

function getGenericError(errors) {
    if (!isEmpty(errors)) {
        return { type: "any", message: ERROR_MESSAGES.FIELDS_IN_ERROR };
    }
}

function preauthorizeCardPayment() {
    return (_, getState) => {
        const {
            paymentForm = {},
            subscriptionForm = {}
        } = getState();

        const {
            cardInformation: {
                customer: customerId,
                id: cardId
            } = {},
            paymentMethod,
            paypalId
        } = paymentForm;

        const {
            selectedProductDetails: {
                ratePlanId
            } = {}
        } = subscriptionForm;

        if (paymentMethod === "stripe") {
            return Payments.addPreAuthCharge(cardId, customerId, ratePlanId);
        } else if (paymentMethod === "paypal") {
            return PayPal.addPreAuthCharge(paypalId, ratePlanId);
        }

        return Promise.resolve();
    };
}

function trackSubmitSubscription({
    id,
    pricing,
    productName,
    productCategory,
    productId,
    billingPeriod,
    offerType
}) {
    GoogleTracking.registerEvent({
        event: "Keywee Event",
        keyweeEventName: "Purchase"
    });
    GoogleTracking.registerSubscriptionEvent("success", "purchase:completed");

    GoogleTracking.registerCommercialTransaction({
        accountId: id,
        price: pricing.totalWithDiscount,
        productName,
        productCategory,
        productId,
        billingPeriod,
        offerType
    });

    optimizely.sendCustomEvent(OPTIMIZELY_CUSTOM_EVENTS.SUBSCRIPTION_SUCCESS);
}

function redirectAfterSubmitSubscription({ isNew, isSignedIn, alreadyHasSubscription, onboardingEnabled }) {
    if (isNew) {
        return browserHistory.push(appPath(SCREENS.SUBSCRIPTION_ACTIVATION_EMAIL, {
            pathname: window.location.pathname,
            search: `?redirectTo=${ SCREENS.SUBSCRIPTION_SUCCESS }`
        }));
    }

    if (!alreadyHasSubscription && isOnboardingEnabled() & onboardingEnabled) {
        return browserHistory.push(appPath(SCREENS.ONBOARDING, {
            pathname: window.location.pathname,
            search: `?existing=true&signedIn=${ isSignedIn }`
        }));
    }

    return browserHistory.push(appPath(SCREENS.SUBSCRIPTION_SUCCESS, {
        pathname: window.location.pathname,
        search: `?existing=true&signedIn=${ isSignedIn }&alreadyHasSubscription=${ alreadyHasSubscription }`
    }));
}

function collectErrorsAndClearState(error) {
    return (dispatch) => {
        if (error) {
            dispatch(setError({ type: "purchaseSubscription", message: error.errorMessage }));
        }

        dispatch(collectSinglePageErrors());
        dispatch(paymentFormActions.clearLoading());
        dispatch(doneThinking());
    };
}

function handleSubmitSubscriptionError(error) {
    return (dispatch, getState) => {
        const {
            loginRegister: {
                email
            } = {}
        } = getState();
        const isIneligible = error.errorMessage === ERROR_MESSAGES.PROMO_NOT_ELIGIBLE;
        const shouldShowError = !isIneligible;

        dispatch(collectErrorsAndClearState(shouldShowError ? error : null));

        if (isIneligible) {
            GoogleTracking.registerSubscriptionEvent("ineligible", "purchase:incomplete");
            dispatch(marketingActions.updateAbandonedCartStatus(email, ELOQUA_ABANDONED_CART_STATUS.SUBSCRIBE_SUCCESS));

            // for functional testing purposes, we want to keep the search if it has skipAbba
            browserHistory.push(appPath(SCREENS.COUNTER_OFFER, {
                search: window.location.search.includes("skipAbba") ? window.location.search : ""
            }));
        }
    };
}

export function submitSubscription(additionalFormFields = {}) {
    return (dispatch, getState) => {
        const {
            abba = {},
            loginRegister = {},
            user = {},
            paymentForm = {},
            billingAddressForm,
            shippingAddressForm,
            studentForm,
            subscriptionForm = {}
        } = getState();

        const {
            selectedProductDetails: {
                pricing = {},
                productName,
                productId,
                productCategory,
                offerType,
                billingPeriod
            } = {}
        } = subscriptionForm;

        const {
            config: {
                onboarding: {
                    onboardingEnabled
                } = {}
            } = {}
        } = abba;

        assign(subscriptionForm.selectedProductDetails, additionalFormFields);

        const email = user.email || loginRegister.email;

        GoogleTracking.registerSubscriptionEvent("submit", "purchase:subscription");

        dispatch(setThinking(THINKING_TYPE_PROCESSING));
        dispatch(clearError());

        return dispatch(preauthorizeCardPayment())
            .then(() => Entitlements.addBloombergSubscription({
                paymentForm: omit(paymentForm, "stripe"), // we don't want to send the whole stripe DOM element
                billingAddressForm,
                shippingAddressForm,
                subscriptionForm,
                studentForm,
                email
            }))
            .then(({ id, isNew, alreadyHasSubscription }) => {
                if (!alreadyHasSubscription) {
                    trackSubmitSubscription({
                        id,
                        pricing,
                        productName,
                        productCategory,
                        productId,
                        billingPeriod,
                        offerType
                    });
                }

                dispatch(marketingActions.updateAbandonedCartStatus(email, ELOQUA_ABANDONED_CART_STATUS.SUBSCRIBE_SUCCESS));
                dispatch(doneThinking());
                dispatch(paymentFormActions.clearLoading());

                return redirectAfterSubmitSubscription({
                    isNew,
                    isSignedIn: !!user.email,
                    alreadyHasSubscription,
                    onboardingEnabled
                });
            })
            .catch((error) => dispatch(handleSubmitSubscriptionError(error)));
    };
}

export default {
    subscriptionFormReducer,
    getPrimaryProductDetails,
    setPrimaryProductDetails,
    getSecondaryProductDetails,
    setSecondaryProductDetails,
    setSelectedProductDetails,
    getPromoCodeInfo,
    setPromoCodeInfo,
    setCancelNoticeChecked,
    getReferralToken,
    updateFirstName,
    updateLastName,
    validateFirstName,
    validateLastName,
    validateIsCancelNoticeChecked,
    setError,
    clearError,
    collectSinglePageErrors,
    submitSubscription
};
